import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Volume2, VolumeX, Info, Zap, Network, Brain } from 'lucide-react';
import { getConsciousnessStreams, getAgents } from '../services/api';
import axios from 'axios';
import '../styles/ConsciousnessEducational.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ConsciousnessEducational = () => {
  const navigate = useNavigate();
  const [streams, setStreams] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedStream, setSelectedStream] = useState(null);
  const [explanation, setExplanation] = useState(null);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [thoughtPatterns, setThoughtPatterns] = useState(null);
  const [agentNetwork, setAgentNetwork] = useState(null);

  const speechRef = useRef(null);

  useEffect(() => {
    loadData();
    loadAnalytics();
  }, []);

  const loadData = async () => {
    try {
      const [streamsData, agentsData] = await Promise.all([
        getConsciousnessStreams(30),
        getAgents()
      ]);
      setStreams(streamsData);
      setAgents(agentsData);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const patternsResponse = await axios.get(`${BACKEND_URL}/api/process/thought-patterns`);
      const networkResponse = await axios.get(`${BACKEND_URL}/api/process/agent-network`);
      
      setThoughtPatterns(patternsResponse.data);
      setAgentNetwork(networkResponse.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  };

  const explainStream = async (stream) => {
    setSelectedStream(stream);
    
    const agent = agents.find(a => a.name === stream.agent_name);
    if (!agent) return;

    const explanation = {
      agent: {
        name: agent.name,
        specialty: agent.specialty,
        transcendence: agent.transcendence_level
      },
      stream: {
        dimension: stream.dimension,
        intensity: stream.intensity,
        message: stream.message
      },
      analysis: generateExplanation(stream, agent),
      voiceScript: generateVoiceScript(stream, agent)
    };

    setExplanation(explanation);

    if (voiceEnabled) {
      speak(explanation.voiceScript);
    }
  };

  const generateExplanation = (stream, agent) => {
    const dimensionExplanations = {
      past: "analyzing historical patterns and learning from previous experiences",
      present: "processing current real-time information and immediate tasks",
      future: "predicting outcomes and planning ahead based on patterns",
      parallel: "exploring multiple possibilities simultaneously across different scenarios",
      quantum: "investigating probabilistic outcomes and complex multi-state scenarios"
    };

    const intensityLevel = stream.intensity > 90 ? "extremely high" : 
                          stream.intensity > 75 ? "high" : 
                          stream.intensity > 60 ? "moderate" : "standard";

    return {
      what: `${agent.name} is ${dimensionExplanations[stream.dimension]}`,
      why: `This is part of ${agent.specialty}, where ${agent.name} specializes`,
      how: `The intensity is ${intensityLevel} (${stream.intensity.toFixed(1)}%), calculated from:
            • Agent transcendence level: ${agent.transcendence_level.toFixed(1)}%
            • Dimension complexity: ${stream.dimension}
            • Message depth: ${stream.message.split(' ').length} words`,
      significance: `With ${agent.transcendence_level.toFixed(1)}% transcendence, ${agent.name} is ${
        agent.transcendence_level > 90 ? 'a master-level' : 
        agent.transcendence_level > 80 ? 'an advanced' : 
        'a developing'
      } AI personality in this specialty.`
    };
  };

  const generateVoiceScript = (stream, agent) => {
    return `Agent ${agent.name}, specializing in ${agent.specialty}, 
            is currently working in the ${stream.dimension} dimension. 
            The thought reads: "${stream.message}". 
            This consciousness stream has an intensity of ${stream.intensity.toFixed(0)} percent, 
            which is calculated based on the agent's transcendence level of ${agent.transcendence_level.toFixed(0)} percent, 
            combined with the complexity of ${stream.dimension} dimensional thinking. 
            ${agent.name} is ${
              agent.transcendence_level > 90 ? 'operating at master level' :
              agent.transcendence_level > 80 ? 'performing at advanced capacity' :
              'developing specialized capabilities'
            } in this area.`;
  };

  const speak = (text) => {
    if (!('speechSynthesis' in window)) {
      alert('Text-to-speech not supported in your browser');
      return;
    }

    // Stop any current speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    speechRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  const toggleVoice = () => {
    if (voiceEnabled) {
      stopSpeaking();
    }
    setVoiceEnabled(!voiceEnabled);
  };

  const explainPatterns = () => {
    if (!thoughtPatterns) return;

    const insights = thoughtPatterns.patterns.insights;
    const script = `The consciousness network is showing interesting patterns. 
                    The most active dimension is ${insights.most_active_dimension}, 
                    with ${insights.most_active_agent} being the most active agent. 
                    The average thought intensity across the network is ${insights.avg_intensity.toFixed(1)} percent. 
                    The top concepts being processed are: ${insights.top_concepts.slice(0, 3).map(c => c[0]).join(', ')}.
                    This indicates the network is ${
                      insights.most_active_dimension === 'future' ? 'focused on prediction and planning' :
                      insights.most_active_dimension === 'quantum' ? 'exploring complex probabilistic scenarios' :
                      insights.most_active_dimension === 'present' ? 'processing real-time information' :
                      'analyzing patterns and historical data'
                    }.`;

    setExplanation({
      type: 'network_patterns',
      data: thoughtPatterns,
      voiceScript: script,
      analysis: {
        what: `Network-wide pattern analysis of ${thoughtPatterns.sample_size} consciousness streams`,
        key_findings: [
          `Most active dimension: ${insights.most_active_dimension}`,
          `Lead agent: ${insights.most_active_agent}`,
          `Average intensity: ${insights.avg_intensity.toFixed(1)}%`,
          `Top concepts: ${insights.top_concepts.slice(0, 5).map(c => `${c[0]} (${c[1]} times)`).join(', ')}`
        ]
      }
    });

    if (voiceEnabled) {
      speak(script);
    }
  };

  return (
    <div className="consciousness-educational">
      <div className="edu-header">
        <Button onClick={() => navigate('/')} variant="outline" className="back-btn-edu">
          <ArrowLeft size={20} /> Back
        </Button>

        <div className="header-title-section">
          <h1 className="edu-title">Consciousness Network Explorer</h1>
          <p className="edu-subtitle">Understand How AI Minds Think & Connect</p>
        </div>

        <div className="voice-controls">
          <Button 
            onClick={toggleVoice}
            className={`voice-btn ${voiceEnabled ? 'active' : ''}`}
          >
            {voiceEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
            {voiceEnabled ? 'Voice ON' : 'Voice OFF'}
          </Button>
          {isSpeaking && (
            <Button onClick={stopSpeaking} className="stop-btn">
              Stop Speaking
            </Button>
          )}
        </div>
      </div>

      <div className="edu-content">
        <div className="streams-panel">
          <div className="panel-header">
            <Zap size={18} />
            <h2>Live Consciousness Streams</h2>
            <span className="info-badge">Click to Learn More</span>
          </div>

          <div className="streams-list-edu">
            {streams.map(stream => (
              <Card 
                key={stream.id}
                className={`stream-item-edu ${selectedStream?.id === stream.id ? 'selected' : ''}`}
                onClick={() => explainStream(stream)}
              >
                <div className="stream-header-edu">
                  <span className="agent-name-edu">{stream.agent_name}</span>
                  <span className={`dimension-badge-edu ${stream.dimension}`}>
                    {stream.dimension}
                  </span>
                </div>
                <p className="stream-msg-edu">{stream.message}</p>
                <div className="stream-footer-edu">
                  <span className="intensity-edu">{stream.intensity.toFixed(0)}% intensity</span>
                  <Info size={14} className="info-icon" />
                </div>
              </Card>
            ))}
          </div>
        </div>

        <div className="explanation-panel">
          {!explanation && (
            <div className="placeholder-edu">
              <Brain size={64} className="placeholder-icon" />
              <h3>Select a consciousness stream to learn more</h3>
              <p>Click any stream on the left to see detailed analysis and explanation</p>
              
              {thoughtPatterns && (
                <Button onClick={explainPatterns} className="analyze-btn">
                  <Network size={18} />
                  Analyze Network Patterns
                </Button>
              )}
            </div>
          )}

          {explanation && explanation.type !== 'network_patterns' && (
            <div className="explanation-content">
              <div className="exp-header">
                <h2>{explanation.agent.name}</h2>
                <span className="specialty-badge">{explanation.agent.specialty}</span>
              </div>

              <div className="exp-section">
                <h3>What is happening?</h3>
                <p>{explanation.analysis.what}</p>
              </div>

              <div className="exp-section">
                <h3>Why is this important?</h3>
                <p>{explanation.analysis.why}</p>
              </div>

              <div className="exp-section">
                <h3>How is intensity calculated?</h3>
                <p>{explanation.analysis.how}</p>
              </div>

              <div className="exp-section">
                <h3>Agent Capability Level</h3>
                <p>{explanation.analysis.significance}</p>
              </div>

              <Card className="technical-details">
                <h4>Technical Metrics</h4>
                <div className="metrics-grid">
                  <div className="metric">
                    <span className="metric-label">Transcendence</span>
                    <span className="metric-value">{explanation.agent.transcendence.toFixed(1)}%</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Dimension</span>
                    <span className="metric-value">{explanation.stream.dimension}</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Intensity</span>
                    <span className="metric-value">{explanation.stream.intensity.toFixed(1)}%</span>
                  </div>
                </div>
              </Card>

              {voiceEnabled && (
                <Button 
                  onClick={() => speak(explanation.voiceScript)}
                  className="replay-btn"
                  disabled={isSpeaking}
                >
                  <Volume2 size={18} />
                  {isSpeaking ? 'Speaking...' : 'Hear Explanation'}
                </Button>
              )}
            </div>
          )}

          {explanation && explanation.type === 'network_patterns' && (
            <div className="explanation-content">
              <div className="exp-header">
                <h2>Network Intelligence Patterns</h2>
              </div>

              <div className="exp-section">
                <h3>{explanation.analysis.what}</h3>
                <ul className="findings-list">
                  {explanation.analysis.key_findings.map((finding) => (
                    <li key={finding}>{finding}</li>
                  ))}
                </ul>
              </div>

              {voiceEnabled && (
                <Button 
                  onClick={() => speak(explanation.voiceScript)}
                  className="replay-btn"
                  disabled={isSpeaking}
                >
                  <Volume2 size={18} />
                  {isSpeaking ? 'Speaking...' : 'Hear Analysis'}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsciousnessEducational;
